import React from 'react';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { UserRole } from '../../types/permissions';

interface PermissionIconButtonProps extends IconButtonProps {
  permission: UserRole;
  disabledTooltip?: string;
  children: React.ReactNode;
}

export const PermissionIconButton = React.forwardRef<HTMLButtonElement, PermissionIconButtonProps>(({
  permission,
  onClick,
  disabledTooltip,
  children,
  disabled,
  ...iconButtonProps
}, ref) => {
  const { hasPermission } = usePermissions();
  
  const hasRequiredPermission = hasPermission(permission);
  const isDisabled = disabled || !hasRequiredPermission;
  
  const defaultTooltip = !hasRequiredPermission 
    ? `You don't have permission to perform this action. Required role: ${permission}`
    : undefined;
  
  const tooltipText = disabledTooltip || defaultTooltip;
  
  const button = (
    <IconButton
      ref={ref}
      {...iconButtonProps}
      disabled={isDisabled}
      onClick={hasRequiredPermission ? onClick : undefined}
      sx={{
        ...iconButtonProps.sx,
        ...(isDisabled && !hasRequiredPermission && {
          opacity: 0.6,
          cursor: 'not-allowed',
        }),
      }}
    >
      {children}
    </IconButton>
  );
  
  if (tooltipText && isDisabled) {
    return (
      <Tooltip title={tooltipText} arrow>
        <span>{button}</span>
      </Tooltip>
    );
  }
  
  return button;
});

PermissionIconButton.displayName = 'PermissionIconButton';
